<template>
  <section>
    <v-form
      ref="form"
      v-model="valid"
      lazy-validation
    >
      <v-card
        class="form-card"
        elevation="8"
      >
        <!-- ===================================================== -->
        <!-- HEADER                                                -->
        <!-- ===================================================== -->

        <v-card-title class="primary white--text">
          <div class="d-flex align-center">
            <v-icon
              color="white"
              class="mr-2"
            >
              mdi-form-select
            </v-icon>

            <span class="font-weight-bold">
              {{ title }}
            </span>
          </div>

          <v-spacer />

          <v-btn-toggle
            v-model="mode"
            mandatory
            dense
            class="designer-toggle"
          >
            <v-btn
              small
              value="config"
            >
              <v-icon
                left
                small
              >
                mdi-cog-outline
              </v-icon>

              Configuração
            </v-btn>

            <v-btn
              small
              value="designer"
            >
              <v-icon
                left
                small
              >
                mdi-view-dashboard-edit-outline
              </v-icon>

              Designer
            </v-btn>
          </v-btn-toggle>
        </v-card-title>

        <!-- ===================================================== -->
        <!-- CONFIGURAÇÃO                                          -->
        <!-- ===================================================== -->

        <v-card-text
          v-if="mode === 'config'"
          class="pt-6"
        >
          <div class="section-title mb-4">
            <v-icon
              color="primary"
              class="mr-2"
            >
              mdi-information-outline
            </v-icon>

            Dados do formulário
          </div>

          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="form.nome"
                label="Nome"
                outlined
                dense
                :rules="[
                  v => !!v || 'O nome é obrigatório.'
                ]"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="form.codigo"
                label="Código"
                outlined
                dense
                hint="Se deixar vazio, será gerado automaticamente."
                persistent-hint
              />
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="form.descricao"
                label="Descrição"
                outlined
                rows="3"
                auto-grow
              />
            </v-col>

            <v-col cols="12">
              <v-switch
                v-model="form.is_active"
                label="Formulário ativo"
                color="primary"
                hide-details
              />
            </v-col>
          </v-row>

          <v-divider class="my-6" />

          <div
            class="d-flex align-center justify-space-between mb-4"
          >
            <div class="section-title">
              <v-icon
                color="primary"
                class="mr-2"
              >
                mdi-form-textbox
              </v-icon>

              Campos do formulário
            </div>

            <v-btn
              color="primary"
              small
              @click="addCampo"
            >
              <v-icon
                left
                small
              >
                mdi-plus
              </v-icon>

              Adicionar campo
            </v-btn>
          </div>

          <v-alert
            v-if="form.campos.length === 0"
            type="info"
            outlined
            dense
          >
            Este formulário ainda não possui campos.
          </v-alert>

          <div
            v-for="(campo, index) in form.campos"
            :key="campo.frontend_id"
            class="campo-card mb-4"
          >
            <div
              class="d-flex align-center justify-space-between campo-header"
            >
              <div class="d-flex align-center">
                <v-icon
                  small
                  color="primary"
                  class="mr-2"
                >
                  mdi-drag
                </v-icon>

                <span class="font-weight-bold">
                  Campo {{ index + 1 }}
                </span>

                <span
                  v-if="campo.label"
                  class="grey--text text--darken-1 ml-2"
                >
                  — {{ campo.label }}
                </span>
              </div>

              <div class="d-flex align-center">
                <v-btn
                  icon
                  small
                  :disabled="index === 0"
                  @click="moveCampoUp(index)"
                >
                  <v-icon small>
                    mdi-arrow-up
                  </v-icon>
                </v-btn>

                <v-btn
                  icon
                  small
                  :disabled="index === form.campos.length - 1"
                  @click="moveCampoDown(index)"
                >
                  <v-icon small>
                    mdi-arrow-down
                  </v-icon>
                </v-btn>

                <v-btn
                  icon
                  small
                  color="error"
                  @click="removeCampo(index)"
                >
                  <v-icon small>
                    mdi-delete
                  </v-icon>
                </v-btn>
              </div>
            </div>

            <v-row class="pt-4">
              <v-col
                cols="12"
                md="5"
              >
                <v-text-field
                  v-model="campo.label"
                  label="Nome do campo"
                  outlined
                  dense
                  :rules="[
                    v => !!v || 'O nome do campo é obrigatório.'
                  ]"
                />
              </v-col>

              <v-col
                cols="12"
                md="4"
              >
                <v-select
                  v-model="campo.tipo_campo"
                  :items="tiposCampo"
                  item-text="text"
                  item-value="value"
                  label="Tipo"
                  outlined
                  dense
                />
              </v-col>

              <v-col
                cols="12"
                md="3"
              >
                <v-select
                  v-model="campo.tamanho"
                  :items="tamanhos"
                  item-text="text"
                  item-value="value"
                  label="Largura"
                  outlined
                  dense
                />
              </v-col>

              <v-col
                cols="12"
                md="6"
              >
                <v-text-field
                  v-model="campo.key"
                  label="Chave"
                  outlined
                  dense
                  hint="Opcional. Se ficar vazia, será gerada automaticamente."
                  persistent-hint
                />
              </v-col>

              <v-col
                cols="12"
                md="3"
              >
                <v-switch
                  v-model="campo.is_required"
                  label="Obrigatório"
                  color="primary"
                  hide-details
                />
              </v-col>

              <v-col
                cols="12"
                md="3"
              >
                <v-switch
                  v-model="campo.is_active"
                  label="Ativo"
                  color="primary"
                  hide-details
                />
              </v-col>

              <!-- ================================================= -->
              <!-- DEFAULT - APENAS UMA UI VISÍVEL                   -->
              <!-- ================================================= -->

              <v-col
                cols="12"
                md="8"
              >
                <v-textarea
                  :value="getSelectedDefaultValue(campo)"
                  label="Valor por defeito"
                  outlined
                  dense
                  auto-grow
                  rows="2"
                  clearable
                  hint="Este valor será automaticamente preenchido quando for criada uma nova formação. Continua a ser editável."
                  persistent-hint
                  @input="setSelectedDefaultValue(campo, $event)"
                />
              </v-col>

              <v-col
                cols="12"
                md="4"
              >
                <v-select
                  v-model="campo.default_editor_tipo_acao"
                  :items="tiposAcaoDefault"
                  item-text="text"
                  item-value="value"
                  label="Preencher por defeito quando Tipo de ação:"
                  outlined
                  dense
                  clearable
                  hint="Sem seleção, aplica-se a todos os tipos de ação."
                  persistent-hint
                />
              </v-col>
            </v-row>
          </div>
        </v-card-text>

        <!-- ===================================================== -->
        <!-- DESIGNER                                              -->
        <!-- ===================================================== -->

        <v-card-text
          v-else
          class="designer-wrapper pa-6"
        >
          <div class="designer-toolbar mb-5">
            <div>
              <div class="section-title">
                <v-icon
                  color="primary"
                  class="mr-2"
                >
                  mdi-view-dashboard-edit-outline
                </v-icon>

                Designer
              </div>

              <div class="grey--text text--darken-1 mt-1">
                Arraste os campos para alterar a ordem.
                Clique num campo para editar as propriedades.
              </div>
            </div>

            <v-btn
              color="primary"
              small
              @click="addCampo"
            >
              <v-icon
                left
                small
              >
                mdi-plus
              </v-icon>

              Adicionar campo
            </v-btn>
          </div>

          <div class="designer-page">
            <div class="designer-form-header">
              <v-icon
                size="34"
                color="primary"
                class="mr-3"
              >
                mdi-school-outline
              </v-icon>

              <div>
                <div class="designer-form-title">
                  {{ form.nome || "Nome do formulário" }}
                </div>

                <div
                  v-if="form.descricao"
                  class="designer-form-description"
                >
                  {{ form.descricao }}
                </div>
              </div>
            </div>

            <v-divider class="my-6" />

            <v-alert
              v-if="activeCampos.length === 0"
              type="info"
              outlined
              dense
            >
              Ainda não existem campos ativos.
            </v-alert>

            <v-row v-else>
              <v-col
                v-for="(campo, index) in activeCampos"
                :key="'designer-' + campo.frontend_id"
                cols="12"
                :md="getPreviewCols(campo)"
                class="designer-col"
              >
                <div
                  class="designer-field"
                  :class="{
                    'designer-field--selected':
                      selectedCampoId === campo.frontend_id,

                    'designer-field--dragging':
                      draggedCampoId === campo.frontend_id
                  }"
                  draggable="true"
                  @dragstart="onDragStart(campo)"
                  @dragover.prevent
                  @dragenter.prevent="onDragEnter(campo)"
                  @dragend="onDragEnd"
                  @click="selectCampo(campo)"
                >
                  <div class="designer-field-toolbar">
                    <div class="d-flex align-center">
                      <v-icon
                        small
                        class="mr-1"
                      >
                        mdi-drag
                      </v-icon>

                      <span class="designer-position">
                        {{ index + 1 }}
                      </span>
                    </div>

                    <v-btn
                      icon
                      x-small
                      color="error"
                      @click.stop="removeCampoById(campo.frontend_id)"
                    >
                      <v-icon small>
                        mdi-delete-outline
                      </v-icon>
                    </v-btn>
                  </div>

                  <v-text-field
                    v-if="campo.tipo_campo === 'text'"
                    :value="getSelectedDefaultValue(campo)"
                    :label="getPreviewLabel(campo)"
                    outlined
                    dense
                    readonly
                    hide-details
                  />

                  <v-textarea
                    v-else-if="campo.tipo_campo === 'textarea'"
                    :value="getSelectedDefaultValue(campo)"
                    :label="getPreviewLabel(campo)"
                    outlined
                    rows="3"
                    auto-grow
                    readonly
                    hide-details
                  />

                  <v-text-field
                    v-else-if="campo.tipo_campo === 'number'"
                    :value="getSelectedDefaultValue(campo)"
                    :label="getPreviewLabel(campo)"
                    type="number"
                    outlined
                    dense
                    readonly
                    hide-details
                  />

                  <v-text-field
                    v-else-if="campo.tipo_campo === 'date'"
                    :value="getSelectedDefaultValue(campo)"
                    :label="getPreviewLabel(campo)"
                    type="date"
                    outlined
                    dense
                    readonly
                    hide-details
                  />

                  <v-text-field
                    v-else-if="campo.tipo_campo === 'time'"
                    :value="getSelectedDefaultValue(campo)"
                    :label="getPreviewLabel(campo)"
                    type="time"
                    outlined
                    dense
                    readonly
                    hide-details
                  />

                  <v-text-field
                    v-else-if="campo.tipo_campo === 'link'"
                    :value="getSelectedDefaultValue(campo)"
                    :label="getPreviewLabel(campo)"
                    prepend-inner-icon="mdi-link"
                    outlined
                    dense
                    readonly
                    hide-details
                  />

                  <div
                    v-else-if="campo.tipo_campo === 'boolean'"
                    class="designer-boolean"
                  >
                    <div class="designer-boolean-label">
                      {{ getPreviewLabel(campo) }}
                    </div>

                    <v-radio-group
                      :value="getSelectedDefaultValue(campo)"
                      row
                      disabled
                      hide-details
                      class="mt-1"
                    >
                      <v-radio
                        label="Sim"
                        value="sim"
                      />

                      <v-radio
                        label="Não"
                        value="nao"
                      />
                    </v-radio-group>
                  </div>

                  <v-text-field
                    v-else
                    :value="getSelectedDefaultValue(campo)"
                    :label="getPreviewLabel(campo)"
                    outlined
                    dense
                    readonly
                    hide-details
                  />

                  <div
                    v-if="getSelectedDefaultValue(campo)"
                    class="designer-default-info mt-2"
                  >
                    <v-icon
                      x-small
                      class="mr-1"
                    >
                      mdi-auto-fix
                    </v-icon>

                    {{ getDefaultContextText(campo) }}
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- ================================================= -->
          <!-- PROPRIEDADES                                      -->
          <!-- ================================================= -->

          <v-expand-transition>
            <v-card
              v-if="selectedCampo"
              class="designer-properties mt-6"
              outlined
            >
              <v-card-title class="pb-2">
                <v-icon
                  color="primary"
                  class="mr-2"
                >
                  mdi-tune
                </v-icon>

                Propriedades do campo

                <v-spacer />

                <v-btn
                  icon
                  small
                  @click="selectedCampoId = null"
                >
                  <v-icon>
                    mdi-close
                  </v-icon>
                </v-btn>
              </v-card-title>

              <v-card-text>
                <v-row>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <v-text-field
                      v-model="selectedCampo.label"
                      label="Nome do campo"
                      outlined
                      dense
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    md="3"
                  >
                    <v-select
                      v-model="selectedCampo.tipo_campo"
                      :items="tiposCampo"
                      item-text="text"
                      item-value="value"
                      label="Tipo"
                      outlined
                      dense
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    md="3"
                  >
                    <v-select
                      v-model="selectedCampo.tamanho"
                      :items="tamanhos"
                      item-text="text"
                      item-value="value"
                      label="Largura"
                      outlined
                      dense
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    md="2"
                  >
                    <v-switch
                      v-model="selectedCampo.is_required"
                      label="Obrigatório"
                      color="primary"
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    md="8"
                  >
                    <v-textarea
                      :value="getSelectedDefaultValue(selectedCampo)"
                      label="Valor por defeito"
                      outlined
                      dense
                      auto-grow
                      rows="3"
                      clearable
                      hint="Será usado para preencher automaticamente este campo numa nova formação."
                      persistent-hint
                      @input="
                        setSelectedDefaultValue(
                          selectedCampo,
                          $event
                        )
                      "
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    md="4"
                  >
                    <v-select
                      v-model="selectedCampo.default_editor_tipo_acao"
                      :items="tiposAcaoDefault"
                      item-text="text"
                      item-value="value"
                      label="Preencher por defeito quando Tipo de ação:"
                      outlined
                      dense
                      clearable
                      hint="Sem seleção, aplica-se a todos os tipos de ação."
                      persistent-hint
                    />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-expand-transition>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-btn
            text
            @click="cancel"
          >
            <v-icon left>
              mdi-arrow-left
            </v-icon>

            Voltar
          </v-btn>

          <v-spacer />

          <v-btn
            v-if="mode === 'config'"
            outlined
            color="primary"
            class="mr-2"
            @click="mode = 'designer'"
          >
            <v-icon left>
              mdi-view-dashboard-edit-outline
            </v-icon>

            Designer
          </v-btn>

          <v-btn
            color="primary"
            :loading="loading"
            @click="submit"
          >
            <v-icon left>
              mdi-content-save
            </v-icon>

            {{ submitText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </section>
</template>

<script>
export default {
  name: "Formulario",

  props: {
    value: {
      type: Object,

      default: () => ({
        nome: "",
        codigo: "",
        descricao: "",
        is_active: true,
        campos: [],
      }),
    },

    loading: {
      type: Boolean,
      default: false,
    },

    title: {
      type: String,
      default: "Formulário de Formação",
    },

    submitText: {
      type: String,
      default: "Guardar",
    },
  },

  data() {
    return {
      valid: true,

      mode: "config",

      selectedCampoId: null,

      draggedCampoId: null,

      form: this.normalizeForm(
        this.value
      ),

      tiposAcaoDefault: [
        {
          text: "Acolhimento",
          value: "acolhimento",
        },
        {
          text: "Sensibilização",
          value: "sensibilizacao",
        },
        {
          text: "Formação local de trabalho",
          value: "formacao_local_trabalho",
        },
      ],

      tiposCampo: [
        {
          text: "Texto",
          value: "text",
        },
        {
          text: "Texto longo",
          value: "textarea",
        },
        {
          text: "Número",
          value: "number",
        },
        {
          text: "Data",
          value: "date",
        },
        {
          text: "Hora",
          value: "time",
        },
        {
          text: "Link",
          value: "link",
        },
        {
          text: "Sim / Não",
          value: "boolean",
        },
      ],

      tamanhos: [
        {
          text: "25%",
          value: 3,
        },
        {
          text: "33%",
          value: 4,
        },
        {
          text: "50%",
          value: 6,
        },
        {
          text: "66%",
          value: 8,
        },
        {
          text: "75%",
          value: 9,
        },
        {
          text: "100%",
          value: 12,
        },
      ],
    };
  },

  computed: {
    activeCampos() {
      return this.form.campos
        .filter((campo) => {
          return campo.is_active !== false;
        })
        .sort((a, b) => {
          return Number(a.posicao || 0)
            - Number(b.posicao || 0);
        });
    },

    selectedCampo() {
      if (!this.selectedCampoId) {
        return null;
      }

      return this.form.campos.find(
        (campo) => {
          return campo.frontend_id
            === this.selectedCampoId;
        }
      ) || null;
    },
  },

  watch: {
    value: {
      deep: true,

      handler(value) {
        this.form =
          this.normalizeForm(
            value
          );
      },
    },
  },

  methods: {
    /*
     * =========================================================
     * NORMALIZAÇÃO
     * =========================================================
     */

    normalizeForm(value) {
      const data =
        value || {};

      return {
        id:
          data.id || null,

        nome:
          data.nome || "",

        codigo:
          data.codigo || "",

        descricao:
          data.descricao || "",

        is_active:
          data.is_active !== undefined
            ? Boolean(
                data.is_active
              )
            : true,

        campos:
          Array.isArray(
            data.campos
          )
            ? data.campos.map(
                (campo, index) => {
                  return this.normalizeCampo(
                    campo,
                    index
                  );
                }
              )
            : [],
      };
    },

    normalizeCampo(
      campo,
      index
    ) {
      const defaults =
        this.normalizeDefaults(
          campo.defaults
        );

      /*
       * Ao editar um formulário existente,
       * mostramos inicialmente o primeiro default
       * configurado.
       *
       * Como o backend coloca o Geral primeiro,
       * se existir Geral será esse.
       */
      const primeiroDefault =
        defaults.length > 0
          ? defaults[0]
          : null;

      return {
        frontend_id:
          campo.frontend_id
          || campo.id
          || this.generateFrontendId(),

        id:
          campo.id || null,

        key:
          campo.key || "",

        label:
          campo.label || "",

        tipo_campo:
          campo.tipo_campo
          || "text",

        is_required:
          campo.is_required !== undefined
            ? Boolean(
                campo.is_required
              )
            : false,

        posicao:
          campo.posicao !== undefined
            ? Number(
                campo.posicao
              )
            : index + 1,

        tamanho:
          campo.tamanho !== undefined
          && campo.tamanho !== null
            ? Number(
                campo.tamanho
              )
            : 12,

        /*
         * Todos os defaults reais ficam guardados aqui.
         */
        defaults,

        /*
         * Isto serve apenas para o Designer saber
         * qual default está neste momento a editar.
         *
         * null = Geral.
         */
        default_editor_tipo_acao:
          primeiroDefault
            ? primeiroDefault.tipo_acao || null
            : null,

        is_active:
          campo.is_active !== undefined
            ? Boolean(
                campo.is_active
              )
            : true,
      };
    },

    normalizeDefaults(defaults) {
      if (!Array.isArray(defaults)) {
        return [];
      }

      return defaults.map(
        (item) => ({
          id:
            item.id
            || null,

          tipo_acao:
            item.tipo_acao
            || null,

          default_value:
            item.default_value
            ?? "",
        })
      );
    },

    generateFrontendId() {
      return (
        Date.now().toString()
        + "-"
        + Math.random()
          .toString(36)
          .substring(2, 10)
      );
    },

    /*
     * =========================================================
     * DEFAULTS
     * =========================================================
     */

    getDefaultForTipo(
      campo,
      tipoAcao
    ) {
      if (
        !campo
        || !Array.isArray(
          campo.defaults
        )
      ) {
        return null;
      }

      const normalizedTipo =
        tipoAcao
        || null;

      return campo.defaults.find(
        (item) => {
          return (
            item.tipo_acao
            || null
          ) === normalizedTipo;
        }
      ) || null;
    },

    getSelectedDefaultValue(
      campo
    ) {
      if (!campo) {
        return "";
      }

      const item =
        this.getDefaultForTipo(
          campo,
          campo.default_editor_tipo_acao
        );

      if (!item) {
        return "";
      }

      return item.default_value
        ?? "";
    },

    setSelectedDefaultValue(
      campo,
      value
    ) {
      if (!campo) {
        return;
      }

      if (
        !Array.isArray(
          campo.defaults
        )
      ) {
        this.$set(
          campo,
          "defaults",
          []
        );
      }

      const tipoAcao =
        campo.default_editor_tipo_acao
        || null;

      const index =
        campo.defaults.findIndex(
          (item) => {
            return (
              item.tipo_acao
              || null
            ) === tipoAcao;
          }
        );

      /*
       * Se o utilizador apagar o conteúdo,
       * removemos esse default específico.
       */
      if (
        value === null
        || value === undefined
        || value === ""
      ) {
        if (
          index !== -1
        ) {
          campo.defaults.splice(
            index,
            1
          );
        }

        return;
      }

      /*
       * Já existe default para este Tipo de Ação.
       */
      if (
        index !== -1
      ) {
        this.$set(
          campo.defaults[index],
          "default_value",
          value
        );

        return;
      }

      /*
       * Ainda não existe:
       * criamos novo.
       */
      campo.defaults.push({
        id:
          null,

        tipo_acao:
          tipoAcao,

        default_value:
          value,
      });
    },

    getDefaultContextText(
      campo
    ) {
      if (
        !campo
        || !campo.default_editor_tipo_acao
      ) {
        return "Valor por defeito geral";
      }

      const tipo =
        this.tiposAcaoDefault.find(
          (item) => {
            return item.value
              === campo.default_editor_tipo_acao;
          }
        );

      if (!tipo) {
        return "Valor por defeito";
      }

      return (
        "Valor por defeito para "
        + tipo.text
      );
    },

    /*
     * =========================================================
     * CAMPOS
     * =========================================================
     */

    addCampo() {
      const novoCampo = {
        frontend_id:
          this.generateFrontendId(),

        id:
          null,

        key:
          "",

        label:
          "Novo campo",

        tipo_campo:
          "text",

        is_required:
          false,

        posicao:
          this.form.campos.length
          + 1,

        tamanho:
          12,

        defaults:
          [],

        /*
         * Começa no default Geral.
         */
        default_editor_tipo_acao:
          null,

        is_active:
          true,
      };

      this.form.campos.push(
        novoCampo
      );

      this.refreshPositions();

      if (
        this.mode
        === "designer"
      ) {
        this.selectedCampoId =
          novoCampo.frontend_id;
      }
    },

    removeCampo(index) {
      const campo =
        this.form.campos[index];

      if (
        campo
        && this.selectedCampoId
          === campo.frontend_id
      ) {
        this.selectedCampoId =
          null;
      }

      this.form.campos.splice(
        index,
        1
      );

      this.refreshPositions();
    },

    removeCampoById(
      frontendId
    ) {
      const index =
        this.form.campos
          .findIndex(
            (campo) => {
              return campo.frontend_id
                === frontendId;
            }
          );

      if (
        index === -1
      ) {
        return;
      }

      this.removeCampo(
        index
      );
    },

    moveCampoUp(index) {
      if (
        index <= 0
      ) {
        return;
      }

      this.moveCampo(
        index,
        index - 1
      );
    },

    moveCampoDown(index) {
      if (
        index
        >= this.form.campos.length
          - 1
      ) {
        return;
      }

      this.moveCampo(
        index,
        index + 1
      );
    },

    moveCampo(
      fromIndex,
      toIndex
    ) {
      if (
        fromIndex
        === toIndex
      ) {
        return;
      }

      const campos = [
        ...this.form.campos,
      ];

      const campo =
        campos.splice(
          fromIndex,
          1
        )[0];

      campos.splice(
        toIndex,
        0,
        campo
      );

      this.form.campos =
        campos;

      this.refreshPositions();
    },

    refreshPositions() {
      this.form.campos =
        this.form.campos.map(
          (campo, index) => ({
            ...campo,

            posicao:
              index + 1,
          })
        );
    },

    selectCampo(campo) {
      this.selectedCampoId =
        campo.frontend_id;
    },

    getPreviewLabel(campo) {
      const label =
        campo.label
        || "Campo sem nome";

      if (
        campo.is_required
      ) {
        return label
          + " *";
      }

      return label;
    },

    getPreviewCols(campo) {
      const tamanho =
        Number(
          campo.tamanho || 12
        );

      const allowed = [
        3,
        4,
        6,
        8,
        9,
        12,
      ];

      if (
        allowed.includes(
          tamanho
        )
      ) {
        return tamanho;
      }

      return 12;
    },

    /*
     * =========================================================
     * DRAG AND DROP
     * =========================================================
     */

    onDragStart(campo) {
      this.draggedCampoId =
        campo.frontend_id;

      this.selectedCampoId =
        campo.frontend_id;
    },

    onDragEnter(
      targetCampo
    ) {
      if (
        !this.draggedCampoId
      ) {
        return;
      }

      if (
        this.draggedCampoId
        === targetCampo.frontend_id
      ) {
        return;
      }

      const fromIndex =
        this.form.campos
          .findIndex(
            (campo) => {
              return campo.frontend_id
                === this.draggedCampoId;
            }
          );

      const toIndex =
        this.form.campos
          .findIndex(
            (campo) => {
              return campo.frontend_id
                === targetCampo.frontend_id;
            }
          );

      if (
        fromIndex === -1
        || toIndex === -1
      ) {
        return;
      }

      this.moveCampo(
        fromIndex,
        toIndex
      );
    },

    onDragEnd() {
      this.draggedCampoId =
        null;

      this.refreshPositions();
    },

    /*
     * =========================================================
     * SUBMIT
     * =========================================================
     */

    submit() {
      const valid =
        this.$refs.form
          .validate();

      if (!valid) {
        this.mode =
          "config";

        return;
      }

      this.refreshPositions();

      const payload = {
        nome:
          this.form.nome,

        codigo:
          this.form.codigo
          || null,

        descricao:
          this.form.descricao
          || null,

        is_active:
          Boolean(
            this.form.is_active
          ),

        campos:
          this.form.campos.map(
            (campo, index) => ({
              id:
                campo.id
                || undefined,

              key:
                campo.key
                || null,

              label:
                campo.label,

              tipo_campo:
                campo.tipo_campo,

              is_required:
                Boolean(
                  campo.is_required
                ),

              posicao:
                index + 1,

              tamanho:
                campo.tamanho
                || 12,

              is_active:
                Boolean(
                  campo.is_active
                ),

              /*
               * Agora enviamos TODOS os defaults
               * que tenham conteúdo.
               */
              defaults:
                Array.isArray(
                  campo.defaults
                )
                  ? campo.defaults
                      .filter(
                        (item) => {
                          return (
                            item.default_value !== null
                            && item.default_value !== undefined
                            && String(
                              item.default_value
                            ).trim() !== ""
                          );
                        }
                      )
                      .map(
                        (item) => ({
                          tipo_acao:
                            item.tipo_acao
                            || null,

                          default_value:
                            item.default_value,
                        })
                      )
                  : [],
            })
          ),
      };

      this.$emit(
        "submit",
        payload
      );
    },

    cancel() {
      this.$emit(
        "cancel"
      );
    },
  },
};
</script>

<style scoped>
.form-card {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 17px;
  font-weight: 800;
}

.campo-card {
  border: 1px solid rgba(0, 0, 0, 0.10);
  border-radius: 12px;
  padding: 16px;
  background: #fff;
}

.campo-header {
  background: rgba(0, 0, 0, 0.025);
  margin: -16px -16px 0 -16px;
  padding: 10px 14px;
  border-radius: 12px 12px 0 0;
}

.designer-toggle {
  background: white;
}

.designer-toggle :deep(.v-btn) {
  color: var(--v-primary-base);
}

.designer-wrapper {
  background: #f5f6f8;
}

.designer-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.designer-page {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.10);
  border-radius: 16px;
  padding: 28px;
  min-height: 400px;
}

.designer-form-header {
  display: flex;
  align-items: flex-start;
}

.designer-form-title {
  font-size: 25px;
  line-height: 1.25;
  font-weight: 800;
  color: var(--v-primary-base);
}

.designer-form-description {
  margin-top: 7px;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.60);
  white-space: pre-line;
}

.designer-col {
  transition: all 0.15s ease;
}

.designer-field {
  position: relative;
  padding: 34px 12px 12px;
  border: 2px solid transparent;
  border-radius: 10px;
  cursor: pointer;

  transition:
    border-color 0.15s ease,
    background-color 0.15s ease,
    opacity 0.15s ease;
}

.designer-field:hover {
  border-color:
    rgba(25, 118, 210, 0.25);

  background:
    rgba(25, 118, 210, 0.025);
}

.designer-field--selected {
  border-color:
    var(--v-primary-base);

  background:
    rgba(25, 118, 210, 0.04);
}

.designer-field--dragging {
  opacity: 0.45;
}

.designer-field-toolbar {
  position: absolute;
  top: 5px;
  left: 8px;
  right: 5px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  opacity: 0.55;
}

.designer-field:hover
.designer-field-toolbar,
.designer-field--selected
.designer-field-toolbar {
  opacity: 1;
}

.designer-position {
  font-size: 11px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.55);
}

.designer-boolean {
  min-height: 65px;
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
  padding: 9px 12px;
}

.designer-boolean-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.60);
}

.designer-default-info {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.55);
}

.designer-properties {
  border-radius: 14px;
  background: white;
}

@media (max-width: 960px) {
  .designer-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .designer-page {
    padding: 18px;
  }
}

@media (max-width: 600px) {
  .designer-form-title {
    font-size: 21px;
  }

  .designer-toggle :deep(.v-btn) {
    min-width: auto;
    padding: 0 8px;
  }
}
</style>